import { callDecimal } from "../utils.ts";
import { SystemKind } from "../common-types/kind.ts";

interface Subject {
  name: string;
}

export interface TocEntry {
  title: string;
  level: number;
  page: string;
}

export type TaxCategory = "Printed Books" | "Gift Cards";

/**
 * Price strings may not have trailing zeros, e.g. "99.0"
 */
export interface BaseVariant {
  id: number;
  inStore: boolean;
  // Not sure about other values
  itemType: "Single" | "Rental";
  purchaseable: boolean;
  // null for free books
  studentPrice?: string;
  // null for free books
  studentCurrency?: string;
  sku: string;
  taxCategory: TaxCategory;

  // Not sure about digital/print uses, I think rental is coming later
  // note use of "unknown" currently causes issues in my courses stories
  rentalEndAt: never;
  returnAt: never;
}

export interface DigitalVariant extends BaseVariant {
  condition: null;
  // Other values are e.g. "180 days"
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  downloadableDuration: "perpetual" | string;
  // e.g. "250 days"
  onlineDuration: string;
  orderType: null;
  originalSku: null;
  productAvailability: null;
  // returnable does exist, but the value is wrong. it's a print-only value at the moment.
  // omitting seems the easiest way to handle int he type
  stockUpdatedAt: null;
  source: null;
  stockOnHand: null;
}

export type Condition = "new" | "used" | "marketplace";
export type ProductSource = "Akademos" | "Manage";
export type ProductAvailability =
  | "Available"
  | "Awaiting stock"
  | "Temporarily unavailable"
  | "Out of stock";

export interface PrintVariant extends BaseVariant {
  condition: Condition;
  // Populated but does not make sense for print
  downloadableDuration: never;
  // Populated but does not make sense for print
  onlineDuration: never;
  orderType: "purchase" | "rental";
  originalSku: string;
  productAvailability: ProductAvailability;
  returnable: boolean;
  stockUpdatedAt: null | string;
  source: ProductSource;
  stockOnHand: number;
}

export type BookFormat = "ePub" | "Page Fidelity" | "DashML";

export interface ReturnPolicy {
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  name: "VitalSource" | string;
  percentView: number;
  returnItemTotal: number;
  returnPastDays: number;
  returnWithinDays: number;
  unusedCodeWithinDays?: number;
}

export interface BaseProduct<Variant> {
  aliases?: string;
  author: string;
  canonicalIsbn: string;
  copyrightYear?: number;
  description: string;
  edition: number;
  hasCoachMe: boolean;
  imprintName?: string;
  // e.g. archie-lovell-a-novel-edwards-annie-v9781241368586
  slug: string;
  subjects: Subject[];
  subtitle?: string;
  title: string;
  variants: Variant[];
  vbid: string;
}

export interface DigitalProduct extends BaseProduct<DigitalVariant> {
  // null for packages
  bookFormat: BookFormat | null;
  // empty string for no separate canonicalPrintIsbn
  canonicalPrintIsbn: string;
  kind: SystemKind;
  productComposition: null;
  productForm: null;
  productFormDetails: null;
  publishingStatus: null;
  returnPolicy: ReturnPolicy;
  tableOfContents?: { items: TocEntry[] };
}

export interface PrintProduct extends BaseProduct<PrintVariant> {
  bookFormat: null;
  canonicalPrintIsbn: null;
  kind: "book";
  productComposition: string;
  productForm: string;
  productFormDetails: never;
  publishingStatus: string;
  // No backend available for print return policy. If it's here, it's the
  // default digital policy.
  returnPolicy: null;
  tableOfContents: null;
}

export type Product = DigitalProduct | PrintProduct;
export type Variant = DigitalVariant | PrintVariant;

export function getProductDetails(vbid: string) {
  return callDecimal<Product[]>(`/api/v1/products/${vbid}`);
}

export async function getBatchProductDetails(vbids: string[]) {
  // some talk of a batch api but for now just do it in parallel
  const results = await Promise.allSettled(
    vbids.map((vbid) => getProductDetails(vbid))
  );

  return results.reduce<Record<string, Product[]>>((acc, cur, i) => {
    if (cur.status === "fulfilled" && cur.value.data) {
      acc[vbids[i]] = cur.value.data;
    }
    return acc;
  }, {});
}

export interface MarketplaceItem {
  comments: string | null;
  condition: string;
  location: string;
  productId: string;
  purchaseType: unknown; // "purchase"
  quantity: number;
  returnable: boolean;
  sellerName: string;
  // numeric
  sellerRating: string;
  // e.g. 0.89
  unitCost: string;
  // e.g. 1.11
  unitPriceRaw: string;
}

export function getMarketplaceOptions(vbid: string) {
  return callDecimal<MarketplaceItem[]>(`/api/v1/marketplace/${vbid}`);
}
